import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useSortable, SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronRight, GripVertical, Edit, Trash2, Grid } from 'lucide-react'
import { IDataGridComponent, FormComponentType, IFormControlChange, DataGridEntry } from '../df-form-preview-interfaces'
import './df-form-grid.scss'

// Import form control components for rendering children
import { DfFormInput } from '../df-form-input'
import { DfFormTextarea } from '../df-form-textarea'
import { DfFormSelect } from '../df-form-select'
import { DfFormCheckbox } from '../df-form-checkbox'
import { DfFormRadio } from '../df-form-radio'
import { DfFormDateTime } from '../df-form-date-time'
import { DfFormSignature } from '../df-form-signature'
import { DfFormHeading } from '../df-form-heading'
import { DfFormInstruction } from '../df-form-instruction'

interface DfFormDataGridProps {
  id: string
  properties: IDataGridComponent
  validationErrors?: Record<string, any>
  formValue?: any
  formData?: Record<string, any>
  readonly?: boolean
  disabled?: boolean
  touchedFields?: Record<string, boolean>
  formSubmitted?: boolean
  mode?: 'edit' | 'preview' | 'test'
  onValueChange?: (change: IFormControlChange) => void
  onBlur?: () => void
  onFocus?: () => void
  onSelect?: () => void
  isSelected?: boolean
  className?: string
  // DataGrid-specific props
  onDataGridSelect?: (dataGrid: IDataGridComponent) => void
  onDataGridDelete?: (dataGridId: string) => void
  onEntryChange?: (entryIndex: number, components: FormComponentType[]) => void
  onComponentSelect?: (component: FormComponentType) => void
  onComponentDelete?: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  onComponentUpdate?: (componentId: string, updates: Partial<FormComponentType>) => void
  selectedComponent?: FormComponentType | null
  renderFormComponent?: (field: FormComponentType, hideLabel?: boolean) => React.ReactNode
  // Additional props for consistency with other components
  onDataGridUpdate?: (dataGridId: string, updates: Partial<IDataGridComponent>) => void
  onEntryAdd?: () => void
  onEntryRemove?: (entryIndex: number) => void
}

// Draggable component for items inside grid entries
interface DraggableGridComponentProps {
  component: FormComponentType
  selectedComponent: FormComponentType | null
  mode: 'edit' | 'preview' | 'test'
  onComponentSelect: (component: FormComponentType) => void
  onComponentDelete: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  onComponentUpdate?: (componentId: string, updates: Partial<FormComponentType>) => void
  renderFormComponent: (field: FormComponentType) => React.ReactNode
  isOverlay?: boolean
}

const DraggableGridComponent: React.FC<DraggableGridComponentProps> = ({
  component,
  selectedComponent,
  mode,
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  renderFormComponent,
  isOverlay = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: component.id,
    disabled: isOverlay,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        border: selectedComponent?.id === component.id ? '2px solid #3b82f6' : '1px solid var(--df-color-fb-border)',
        borderRadius: '8px',
        backgroundColor: 'var(--df-color-fb-container)',
        padding: '12px',
        position: 'relative',
        minHeight: '80px',
        transition: 'all 0.2s ease',
        boxShadow: selectedComponent?.id === component.id ? '0 4px 12px rgba(59, 130, 246, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer'
      }}
      className={`form-component grid-component ${selectedComponent?.id === component.id ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isSorting ? 'sorting' : ''}`}
      onClick={() => !isDragging && onComponentSelect(component)}
      role="button"
      tabIndex={0}
    >
      {/* Drag Handle - Only visible in edit mode */}
      {mode === 'edit' && (
        <div 
          className="component-drag-handle" 
          {...listeners} 
          {...attributes}
          onClick={(e) => e.stopPropagation()}
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '4px',
            transform: 'translateY(-50%)',
            padding: '4px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            cursor: 'grab',
            opacity: 1,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <GripVertical size={12} color="var(--df-color-text-light)" />
        </div>
      )}

      {/* Component Content */}
      <div 
        className="component-content"
        style={{
          paddingTop: mode === 'edit' ? '24px' : '0',
          paddingLeft: mode === 'edit' ? '28px' : '0',
          minHeight: '60px'
        }}
      >
        {renderFormComponent(component)}
      </div>

      {/* Action Buttons */}
      {mode === 'edit' && (
        <div 
          className="component-actions" 
          style={{ 
            position: 'absolute',
            top: '4px',
            right: '4px',
            display: 'flex',
            gap: '4px',
            opacity: 1,
            zIndex: 10
          }}
        >
          {/* Edit Button */}
          {onComponentEdit && (
            <button
              className="component-edit-btn"
              onClick={(e) => {
                e.stopPropagation()
                onComponentEdit(component)
              }}
              type="button"
              title="Edit Component"
              style={{
                padding: '4px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6'
              }}
            >
              <Edit size={12} />
            </button>
          )}
          
          {/* Delete Button */}
          <button
            className="component-delete-btn"
            onClick={(e) => {
              e.stopPropagation()
              onComponentDelete(component, e)
            }}
            type="button"
            title="Delete Component"
            style={{
              padding: '4px',
              backgroundColor: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444'
            }}
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

// Simple Grid Drop Zone Component
interface GridDropZoneProps {
  gridComponents: FormComponentType[]
  mode: 'edit' | 'preview' | 'test'
  onComponentSelect: (component: FormComponentType) => void
  onComponentDelete: (component: FormComponentType, event: React.MouseEvent) => void
  onComponentEdit?: (component: FormComponentType) => void
  onComponentUpdate?: (componentId: string, updates: Partial<FormComponentType>) => void
  selectedComponent: FormComponentType | null
  renderFormComponent: (field: FormComponentType) => React.ReactNode
  gridId: string // Add unique grid ID prop
}

const GridDropZone: React.FC<GridDropZoneProps> = ({
  gridComponents,
  mode,
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  onComponentUpdate,
  selectedComponent,
  renderFormComponent,
  gridId
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `grid-drop-zone-${gridId}`,
    disabled: mode !== 'edit'
  })

  return (
    <div
      ref={setNodeRef}
      className="grid-drop-zone"
      style={{
        border: isOver ? '2px dashed #3b82f6' : '2px dashed #d1d5db',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: isOver ? 'var(--df-color-primary-light)' : 'var(--df-color-fb-container)',
        minHeight: '120px',
        transition: 'all 0.2s ease',
        position: 'relative'
      }}
    >
      {gridComponents.length > 0 ? (
        <>
          <SortableContext items={gridComponents.map(c => c.id)} strategy={horizontalListSortingStrategy}>
            <div style={{ 
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: '12px',
              alignItems: 'start',
              marginBottom: gridComponents.length > 0 ? '12px' : '0',
              overflowX: 'auto',
              width: '100%'
            }}>
              {gridComponents.map((component) => (
                <div key={component.id} style={{ flex: '1 1 0', minWidth: '200px', maxWidth: '100%' }}>
                  <DraggableGridComponent
                    component={component}
                    selectedComponent={selectedComponent}
                    mode={mode}
                    onComponentSelect={onComponentSelect}
                    onComponentDelete={onComponentDelete}
                    onComponentEdit={onComponentEdit}
                    onComponentUpdate={onComponentUpdate}
                    renderFormComponent={renderFormComponent}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
          
          {/* Additional Drop Zone for More Components */}
          <div style={{
            border: isOver ? '2px dashed #3b82f6' : '1px dashed var(--df-color-fb-border)',
            borderRadius: '6px',
            padding: '12px',
            backgroundColor: isOver ? 'var(--df-color-primary-light)' : 'var(--df-color-fb-container)',
            textAlign: 'center',
            color: 'var(--df-color-text-light)',
            fontSize: '12px',
            transition: 'all 0.2s ease',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isOver ? (
              <span style={{ color: '#3b82f6', fontWeight: '500' }}>
                Drop component here to add to grid
              </span>
            ) : (
              <span>
                + Drop more components here
              </span>
            )}
          </div>
        </>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          color: 'var(--df-color-text-light)', 
          fontSize: '14px',
          padding: '40px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: 'var(--df-color-fb-container)',
          border: '1px dashed var(--df-color-fb-border)',
          borderRadius: '8px'
        }}>
          {/* <div style={{ 
            fontSize: '24px',
            opacity: 0.5
          }}>
            📋
          </div> */}
          <div style={{ 
            fontWeight: '500',
            color: isOver ? 'var(--df-color-primary)' : 'var(--df-color-text-dark)'
          }}>
            {isOver ? 'Drop components here' : 'Empty DataGrid'}
          </div>
          <div style={{ 
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            Drag and drop components here to create your grid
          </div>
        </div>
      )}
    </div>
  )
}

// Table View Component for Preview/Test Mode
interface TableViewProps {
  templateComponents: FormComponentType[]
  dataEntries: any[]
  renderFormComponent: (field: FormComponentType, hideLabel?: boolean) => React.ReactNode
  mode?: 'edit' | 'preview' | 'test'
  allowAddRemoveEntries?: boolean
  addAnotherText?: string
  removeText?: string
  maxEntries?: number
  minEntries?: number
  displayAsGrid?: boolean
  onAddEntry?: () => void
  onRemoveEntry?: (entryIndex: number) => void
  formData?: Record<string, any>
}

const TableView: React.FC<TableViewProps> = ({
  templateComponents,
  dataEntries,
  renderFormComponent,
  mode = 'preview',
  allowAddRemoveEntries = true,
  addAnotherText = 'Add Another',
  removeText = 'Remove',
  maxEntries = 10,
  minEntries = 1,
  displayAsGrid = true,
  onAddEntry,
  onRemoveEntry,
  formData: _formData = {}
}) => {

  if (templateComponents.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: 'var(--df-color-text-light)',
        border: '1px dashed #d1d5db',
        borderRadius: '8px',
        backgroundColor: 'var(--df-color-fb-container)'
      }}>
        No components defined. Add components to see the {displayAsGrid ? 'table' : 'list'} view.
      </div>
    )
  }

  // Render as Grid/Table View
  if (displayAsGrid) {
    return (
      <div className="datagrid-table-view" style={{ 
        border: '1px solid var(--df-color-fb-border)', 
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: 'var(--df-color-fb-container)',
        maxWidth: '100%'
      }}>
        {/* Scroll Indicator */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
          fontSize: '12px',
          color: 'var(--df-color-text-light)',
          backgroundColor: 'var(--df-color-fb-container)',
          padding: '4px 8px',
          borderRadius: '4px',
          border: '1px solid var(--df-color-fb-border)',
          opacity: templateComponents.length > 4 ? 1 : 0,
          transition: 'opacity 0.2s ease'
        }}>
          ← Scroll →
        </div>

        {/* Horizontal Scrollable Container */}
        <div 
          className="table-scroll-container"
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--df-color-fb-border) var(--df-color-fb-icon-bg)',
            position: 'relative'
          }}
        >
          {/* Table Headers */}
          <div className="table-headers" style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${templateComponents.length}, minmax(150px, 1fr))`,
            backgroundColor: 'var(--df-color-fb-container)',
            borderBottom: '2px solid var(--df-color-fb-border)',
            minWidth: `${templateComponents.length * 150}px`
          }}>
            {templateComponents.map((component, index) => (
              <div key={`header-${component.id}`} style={{
                padding: '12px 16px',
                fontWeight: '600',
                color: 'var(--df-color-text-dark)',
                borderRight: index < templateComponents.length - 1 ? '1px solid var(--df-color-fb-border)' : 'none',
                fontSize: '14px',
                minWidth: '150px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {component.basic?.label || `Column ${index + 1}`}
              </div>
            ))}
          </div>
      
          {/* Table Rows */}
          {dataEntries.length > 0 ? (
            dataEntries.map((entry, entryIndex) => (
              <div key={entry.id} className="table-row" style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${templateComponents.length}, minmax(150px, 1fr))`,
                borderBottom: entryIndex < dataEntries.length - 1 ? '1px solid var(--df-color-fb-border)' : 'none',
                backgroundColor: entryIndex % 2 === 0 ? 'var(--df-color-fb-container)' : 'var(--df-color-fb-bg)',
                position: 'relative',
                minWidth: `${templateComponents.length * 150}px`
              }}>
            {templateComponents.map((templateComponent, componentIndex) => {
              // Find the corresponding component in this entry by position/index, not by name/label
              // This ensures each template component maps to the correct entry component
              let entryComponent = entry.components?.[componentIndex]
              
              // If no component found at this index, create one from the template
              if (!entryComponent) {
                // Generate unique ID only for new components
                const uniqueId = `${templateComponent.id}-entry-${entryIndex}-${componentIndex}`
                entryComponent = {
                  ...templateComponent,
                  id: uniqueId,
                  basic: {
                    ...templateComponent.basic,
                    showLabel: false // Hide label in datagrid cells
                  }
                }
              } else {
                // Preserve the original ID to maintain form value connections
                entryComponent = {
                  ...entryComponent,
                  id: entryComponent.id, // Keep the original ID
                  basic: {
                    ...entryComponent.basic,
                    showLabel: false // Hide label in datagrid cells
                  }
                }
              }
              
                return (
                  <div key={`${entry.id}-${componentIndex}`} style={{
                    padding: '12px 16px',
                    borderRight: componentIndex < templateComponents.length - 1 ? '1px solid var(--df-color-fb-border)' : 'none',
                    minHeight: '60px',
                    minWidth: '150px',
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '100%',
                      minWidth: '120px',
                      overflow: 'hidden'
                    }}>
                      {renderFormComponent(entryComponent, true)}
                    </div>
                  </div>
                )
            })}
            
            {/* Remove button for each row when more than 1 entry exists */}
            {mode === 'test' && allowAddRemoveEntries && dataEntries.length > minEntries && (
              <button
                onClick={() => onRemoveEntry?.(entryIndex)}
                disabled={dataEntries.length <= minEntries}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '4px 8px',
                  backgroundColor: dataEntries.length <= minEntries ? '#f3f4f6' : '#ef4444',
                  color: dataEntries.length <= minEntries ? '#9ca3af' : '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: dataEntries.length <= minEntries ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  zIndex: 10,
                  minWidth: '24px',
                  height: '24px',
                  justifyContent: 'center'
                }}
                title={removeText}
              >
                <span style={{ fontSize: '14px' }}>×</span>
              </button>
            )}
          </div>
        ))
      ) : (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--df-color-text-light)',
          backgroundColor: 'var(--df-color-fb-container)',
          borderTop: '1px solid var(--df-color-fb-border)'
        }}>
          No data entries. {mode === 'test' && allowAddRemoveEntries ? 'Click "Add Another" to add entries.' : 'Use the properties panel to add entries.'}
        </div>
          )}

        </div>

        {/* Add Another button - Moved outside scrollable container */}
        {mode === 'test' && allowAddRemoveEntries && (
          <div style={{
            padding: '16px',
            backgroundColor: 'var(--df-color-fb-bg)',
            borderTop: '1px solid var(--df-color-fb-border)',
            display: 'flex',
            justifyContent: 'center',
            position: 'sticky',
            bottom: 0,
            zIndex: 5
          }}>
            <button
              onClick={onAddEntry}
              disabled={dataEntries.length >= maxEntries}
              style={{
                padding: '8px 16px',
                backgroundColor: dataEntries.length >= maxEntries ? '#f3f4f6' : '#10b981',
                color: dataEntries.length >= maxEntries ? '#9ca3af' : '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: dataEntries.length >= maxEntries ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span style={{ fontSize: '16px' }}>+</span>
              {addAnotherText}
            </button>
          </div>
        )}
      </div>
    )
  }

  // Render as List/Card View
  return (
    <div className="datagrid-list-view" style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      backgroundColor: 'var(--df-color-fb-container)'
    }}>
      {dataEntries.length > 0 ? (
        dataEntries.map((entry, entryIndex) => (
          <div key={entry.id} className="list-entry" style={{
            border: '1px solid var(--df-color-fb-border)',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: 'var(--df-color-fb-container)',
            position: 'relative',
            transition: 'all 0.2s ease'
          }}>
            {/* Entry Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '1px solid var(--df-color-fb-border)'
            }}>
              <span style={{
                fontWeight: '600',
                color: 'var(--df-color-text-dark)',
                fontSize: '14px'
              }}>
                Entry #{entryIndex + 1}
              </span>
              
              {/* Remove button for each entry when more than 1 entry exists */}
              {mode === 'test' && allowAddRemoveEntries && dataEntries.length > 1 && (
                <button
                  onClick={() => onRemoveEntry?.(entryIndex)}
                  disabled={dataEntries.length <= minEntries}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: dataEntries.length <= minEntries ? '#f3f4f6' : '#ef4444',
                    color: dataEntries.length <= minEntries ? '#9ca3af' : '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: dataEntries.length <= minEntries ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: '24px',
                    height: '24px',
                    justifyContent: 'center'
                  }}
                  title={removeText}
                >
                  <span style={{ fontSize: '14px' }}>×</span>
                </button>
              )}
            </div>

            {/* Entry Fields */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {templateComponents.map((templateComponent, componentIndex) => {
                // Find the corresponding component in this entry by index first, then by name+label
                let entryComponent = entry.components?.[componentIndex]
                
                // If no component at this index, try to find by name+label
                if (!entryComponent) {
                  entryComponent = entry.components?.find((comp: any) => 
                    comp.name === templateComponent.name && 
                    comp.basic?.label === templateComponent.basic?.label
                  )
                }
                
                // If no matching component found, use the template component
                if (!entryComponent) {
                  // Generate unique ID only for new components
                  const uniqueId = `${templateComponent.id}-entry-${entryIndex}-${componentIndex}`
                  entryComponent = {
                    ...templateComponent,
                    id: uniqueId,
                    basic: {
                      ...templateComponent.basic,
                      showLabel: false // Hide label in datagrid cells
                    }
                  }
                } else {
                  // Preserve the original ID to maintain form value connections
                  entryComponent = {
                    ...entryComponent,
                    id: entryComponent.id, // Keep the original ID
                    basic: {
                      ...entryComponent.basic,
                      showLabel: false // Hide label in datagrid cells
                    }
                  }
                }
                
                return (
                  <div key={`${entry.id}-${componentIndex}`} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--df-color-text-light)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {templateComponent.basic?.label || `Field ${componentIndex + 1}`}
                    </label>
                    <div style={{
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {renderFormComponent(entryComponent)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))
      ) : (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--df-color-text-light)',
          backgroundColor: 'var(--df-color-fb-container)',
          border: '1px dashed #d1d5db',
          borderRadius: '8px'
        }}>
          No data entries. {mode === 'test' && allowAddRemoveEntries ? 'Click "Add Another" to add entries.' : 'Use the properties panel to add entries.'}
        </div>
      )}

      {/* Add Another button below the list for test mode */}
      {mode === 'test' && allowAddRemoveEntries && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f8f9fa',
          border: '1px solid var(--df-color-fb-border)',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={onAddEntry}
            disabled={dataEntries.length >= maxEntries}
            style={{
              padding: '8px 16px',
              backgroundColor: dataEntries.length >= maxEntries ? '#f3f4f6' : '#10b981',
              color: dataEntries.length >= maxEntries ? '#9ca3af' : '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: dataEntries.length >= maxEntries ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (dataEntries.length < maxEntries) {
                e.currentTarget.style.backgroundColor = '#059669'
              }
            }}
            onMouseLeave={(e) => {
              if (dataEntries.length < maxEntries) {
                e.currentTarget.style.backgroundColor = '#10b981'
              }
            }}
          >
            <span>+</span>
            {addAnotherText}
          </button>
        </div>
      )}
    </div>
  )
}


export const DfFormDataGrid: React.FC<DfFormDataGridProps> = ({
  id,
  properties,
  mode = 'edit',
  formData = {},
  onValueChange,
  onSelect,
  isSelected = false,
  className = '',
  onDataGridSelect,
  onComponentSelect,
  onComponentDelete,
  onComponentEdit,
  onComponentUpdate,
  selectedComponent,
  renderFormComponent,
  onEntryAdd,
  onEntryRemove
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const hasInitialized = useRef(false)

  // Get all components in the grid
  let gridComponents = properties.templateComponents || []
  const dataEntries = properties.entries || []
  
  
  // Fallback: If no template components but we have entries, extract template from first entry
  if (gridComponents.length === 0 && dataEntries.length > 0 && dataEntries[0].components) {
    gridComponents = dataEntries[0].components.map((comp: any) => ({
      ...comp,
      id: comp.id?.replace(/-entry-\d+$/, '') || comp.id, // Remove entry suffix for template
      basic: {
        ...comp.basic,
        showLabel: true // Show labels in template
      }
    }))
  }

  // Initialize template components if empty (only in edit mode)
  useEffect(() => {
    if (mode === 'edit' && !properties.templateComponents && onValueChange && hasInitialized.current) {
      const newValue = { 
        ...properties, 
        templateComponents: []
      }
      // Only call onValueChange if the data actually changed
      if (JSON.stringify(newValue) !== JSON.stringify(properties)) {
        onValueChange({
          id,
          value: newValue
        })
      }
    }
    hasInitialized.current = true
  }, [properties.templateComponents, id, onValueChange, properties, mode])

  // Sync template components with existing entries and create initial entry if needed
  useEffect(() => {
    if (gridComponents.length > 0) {
      // Only run this effect if we're in edit mode and there are actual changes needed
      if (mode !== 'edit' || !hasInitialized.current) {
        return // Don't run sync logic in preview/test modes or on first render
      }
      
      
      // Force update entries that don't have proper unique IDs
      const needsIdUpdate = dataEntries.some(entry => 
        entry.components?.some((comp: any, index: number) => {
          const expectedId = `${gridComponents[index]?.id}-entry-${entry.index}-${index}`
          return comp.id !== expectedId
        })
      )
      
      if (needsIdUpdate && onValueChange) {
        const updatedEntries = dataEntries.map(entry => ({
          ...entry,
          components: entry.components?.map((comp: any, index: number) => ({
            ...comp,
            id: `${gridComponents[index]?.id}-entry-${entry.index}-${index}`
          })) || []
        }))
        
        // Only call onValueChange if the data actually changed
        const newValue = { ...properties, entries: updatedEntries }
        if (JSON.stringify(newValue) !== JSON.stringify(properties)) {
          onValueChange({
            id,
            value: newValue
          })
        }
        return // Exit early to avoid double processing
      }
      // If no entries exist, create the first entry
      if (dataEntries.length === 0) {
        const firstEntry = {
          id: `entry-${id}-0`,
          index: 0,
          components: gridComponents.map((comp, componentIndex) => ({
            ...comp,
            id: `${comp.id}-entry-0-${componentIndex}`,
            basic: {
              ...comp.basic,
              showLabel: false // Hide label in datagrid cells
            }
          })),
          styles: {}
        }

        if (onValueChange) {
          const newValue = { ...properties, entries: [firstEntry] }
          // Only call onValueChange if the data actually changed
          if (JSON.stringify(newValue) !== JSON.stringify(properties)) {
            onValueChange({
              id,
              value: newValue
            })
          }
        }
      } else {
        // Update existing entries to include all template components and sync their properties
        const needsUpdate = dataEntries.some(entry => {
          // Check if entry is missing any template components or if existing components need updates
          return gridComponents.some((templateComp, componentIndex) => {
            const existingComponent = entry.components?.[componentIndex]
            
            if (!existingComponent) {
              return true // Missing component at this index
            }
            
            // Check if component has proper unique ID
            const expectedId = `${templateComp.id}-entry-${entry.index}-${componentIndex}`
            const hasProperId = existingComponent.id === expectedId
            
            // Check if existing component needs to be updated with new template properties
            // Compare key properties that should be synced
            const needsPropertyUpdate = 
              JSON.stringify((existingComponent.basic as any)?.options) !== JSON.stringify((templateComp.basic as any)?.options) ||
              (existingComponent.basic as any)?.placeholder !== (templateComp.basic as any)?.placeholder ||
              (existingComponent.basic as any)?.defaultValue !== (templateComp.basic as any)?.defaultValue ||
              (existingComponent.basic as any)?.label !== (templateComp.basic as any)?.label ||
              existingComponent.validation?.required !== templateComp.validation?.required
            return !hasProperId || needsPropertyUpdate
          })
        })

        if (needsUpdate && onValueChange) {
          const updatedEntries = dataEntries.map(entry => {
            // Use index-based matching to ensure each template component maps to the correct entry component
            const updatedComponents = gridComponents.map((templateComp, componentIndex) => {
              // Find existing component by index first
              let existingComponent: FormComponentType | undefined = entry.components?.[componentIndex]
              
              // If no component at this index, try to find by name+label (for backward compatibility)
              if (!existingComponent) {
                existingComponent = entry.components?.find((comp: any) => 
                  comp.name === templateComp.name && 
                  comp.basic?.label === templateComp.basic?.label
                ) as FormComponentType | undefined
              }
              
              // Always generate a unique ID for this component
              const uniqueId = `${templateComp.id}-entry-${entry.index}-${componentIndex}`
              
              if (existingComponent) {
                // Update existing component with new template properties while ensuring unique ID and preserving form values
                const updatedComponent = {
                  ...templateComp,
                  id: uniqueId, // Always use the unique ID pattern
                  basic: {
                    ...templateComp.basic,
                    showLabel: false, // Hide label in datagrid cells
                    // Preserve any user-entered values
                    value: (existingComponent.basic as any)?.value || (templateComp.basic as any)?.defaultValue || ''
                  }
                }

                return updatedComponent
              } else {
                // Create new component based on template
                const newComponent = {
                  ...templateComp,
                  id: uniqueId,
                  basic: {
                    ...templateComp.basic,
                    showLabel: false // Hide label in datagrid cells
                  }
                }
                
                return newComponent
              }
            })

            return {
              ...entry,
              components: updatedComponents
            }
          })

          const newValue = { ...properties, entries: updatedEntries }
          // Only call onValueChange if the data actually changed
          if (JSON.stringify(newValue) !== JSON.stringify(properties)) {
            onValueChange({
              id,
              value: newValue
            })
          }
        }
      }
    }
  }, [gridComponents, dataEntries, id, onValueChange, properties, mode, properties.templateComponents])

  const handleDataGridClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    onSelect?.()
    onDataGridSelect?.(properties)
  }, [onSelect, onDataGridSelect, properties])

  const toggleCollapse = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsCollapsed(!isCollapsed)
    onValueChange?.({
      id,
      value: { ...properties, basic: { ...properties.basic, collapsed: !isCollapsed } }
    })
  }

  const handleComponentDelete = useCallback((component: FormComponentType, event: React.MouseEvent) => {
    event.stopPropagation()
    
    // Call the parent's component delete handler first
    if (onComponentDelete) {
      onComponentDelete(component, event)
    }
    
    // Remove from grid template components
    const updatedComponents = gridComponents.filter(comp => comp.id !== component.id)
    
    // Remove from all existing entries by matching component name and label
    const updatedEntries = dataEntries.map(entry => ({
      ...entry,
      components: entry.components?.filter((entryComp: any) => 
        !(entryComp.name === component.name && entryComp.basic?.label === component.basic?.label)
      ) || []
    }))
    
    if (onValueChange) {
      onValueChange({
        id,
        value: { 
          ...properties, 
          templateComponents: updatedComponents,
          entries: updatedEntries
        }
      })
    }
  }, [onComponentDelete, properties, onValueChange, id, gridComponents, dataEntries])

  const handleComponentUpdate = useCallback((componentId: string, updates: Partial<FormComponentType>) => {
    // Call the parent's component update handler first
    if (onComponentUpdate) {
      onComponentUpdate(componentId, updates)
    }
    
    // Update the component in grid template components
    const updatedComponents = gridComponents.map(comp => 
      comp.id === componentId ? { ...comp, ...updates } : comp
    )
    
    if (onValueChange) {
      onValueChange({
        id,
        value: { ...properties, templateComponents: updatedComponents }
      })
    }
  }, [onComponentUpdate, properties, onValueChange, id, gridComponents])

  // Handle component value change for form data updates (test mode)
  const handleComponentValueChange = useCallback((change: IFormControlChange) => {
    if (mode === 'test') {
      // In test mode, update form data through the parent's onValueChange
      // This allows the form data to be updated for interactive components
      if (onValueChange) {
        onValueChange({
          id: change.id,
          value: change.value
        })
      }
    }
    // In edit/preview modes, don't handle value changes as components are read-only
  }, [mode, onValueChange])

  const handleAddEntry = useCallback(() => {
    const newEntry: DataGridEntry = {
      id: `entry-${properties.entries.length}`,
      index: properties.entries.length,
      components: gridComponents.map((comp, componentIndex) => ({
        ...comp,
        id: `${comp.id}-entry-${properties.entries.length}-${componentIndex}`, // Make each component unique per entry and position
        basic: {
          ...comp.basic,
          showLabel: false // Hide label in datagrid cells
        }
      })) as FormComponentType[],
      styles: {}
    }
    
    const updatedEntries = [...properties.entries, newEntry]
    
    if (onValueChange) {
      onValueChange({
        id,
        value: { ...properties, entries: updatedEntries }
      })
    }
    
    onEntryAdd?.()
  }, [properties, onValueChange, id, onEntryAdd, gridComponents])

  const handleRemoveEntry = useCallback((entryIndex: number) => {
    const updatedEntries = properties.entries
      .filter((_, index) => index !== entryIndex)
      .map((entry, index) => ({ ...entry, index, id: `entry-${id}-${index}` }))
    
    if (onValueChange) {
      onValueChange({
        id,
        value: { ...properties, entries: updatedEntries }
      })
    }
  }, [properties, onValueChange, id, onEntryRemove])


  // Use our own render function to ensure proper onComponentUpdate handling
  const renderComponent = useCallback((field: FormComponentType, hideLabel: boolean = false) => {
    const formValue = mode === 'test' ? (formData[field.id] || ('defaultValue' in field.basic ? field.basic.defaultValue || '' : '')) : ('defaultValue' in field.basic ? field.basic.defaultValue || '' : '')
    
    const commonProps = {
      id: field.id,
      properties: field,
      validationErrors: {},
      formValue: formValue,
      touchedFields: {},
      formSubmitted: false,
      mode,
      hideLabel,
      onValueChange: handleComponentValueChange,
      onBlur: () => {},
      onFocus: () => {}
    }

    switch (field.name) {
      case 'text-input':
      case 'email-input':
      case 'number-input':
        return (
          <DfFormInput
            {...commonProps}
            properties={field as any}
            inputType={field.name === 'text-input' ? 'text' : 
                      field.name === 'number-input' ? 'number' : 'email'}
            readonly={mode === 'edit' || mode === 'preview'}
            disabled={mode === 'preview'}
            hideLabel={hideLabel}
          />
        )
      
      case 'textarea':
        return <DfFormTextarea {...commonProps} properties={field as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} hideLabel={hideLabel} />
      
      case 'select':
        // Map basic.options to top-level options for select component
        const selectProps = {
          ...field,
          options: (field as any).basic?.options || []
        }
        return <DfFormSelect {...commonProps} properties={selectProps as any} disabled={mode === 'edit' || mode === 'preview'} hideLabel={hideLabel} />
      
      case 'checkbox':
        // Map basic.options to top-level options for checkbox component
        const checkboxProps = {
          ...field,
          options: (field as any).basic?.options || []
        }
        return <DfFormCheckbox {...commonProps} properties={checkboxProps as any} formValue={[]} disabled={mode === 'edit' || mode === 'preview'} hideLabel={hideLabel} />
      
      case 'radio':
        // Map basic.options to top-level options for radio component
        const radioProps = {
          ...field,
          options: (field as any).basic?.options || []
        }
        return <DfFormRadio {...commonProps} properties={radioProps as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'edit' || mode === 'preview'} hideLabel={hideLabel} />
      
      case 'date':
      case 'date-picker':
        return <DfFormDateTime {...commonProps} properties={field as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} hideLabel={hideLabel} />
      
      case 'datetime-picker':
        return <DfFormDateTime {...commonProps} properties={field as any} readonly={mode === 'edit' || mode === 'preview'} disabled={mode === 'preview'} hideLabel={hideLabel} />
      
      case 'signature':
        return <DfFormSignature {...commonProps} properties={field as any} hideLabel={hideLabel} />
      
      case 'heading':
        return <DfFormHeading {...commonProps} properties={field as any} hideLabel={hideLabel} />
      
      case 'instructions':
        return <DfFormInstruction {...commonProps} properties={field as any} hideLabel={hideLabel} />
      
      default:
        return <div className="unknown-component">Unknown component: {field.name}</div>
    }
  }, [mode, handleComponentValueChange])

  const gridStyle = {
    backgroundColor: properties.styles.backgroundColor || 'var(--df-color-fb-container)',
    borderColor: properties.styles.borderColor || 'var(--df-color-fb-border)',
    borderWidth: properties.styles.borderWidth || '2px',
    borderRadius: properties.styles.borderRadius || '8px',
    padding: properties.styles.padding || '16px',
    margin: properties.styles.margin || '16px 0'
  }

  // Always render the grid - let GridDropZone handle empty state

  return (
    <div
      className={`form-group df-form-datagrid ${isSelected ? 'selected' : ''} ${className}`}
      onClick={handleDataGridClick}
      style={gridStyle}
    >
      {/* Grid Header */}
      <div className="grid-header">
        <div className="grid-title" onClick={toggleCollapse}>
          <div className="title-content" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            <Grid size={16} />
            <span className="grid-label" style={{ fontWeight: '600' }}>{properties.basic.label}</span>
            {properties.validation.required && (
              <span className="required-indicator" style={{ color: '#ef4444' }}>*</span>
            )}
          </div>
        </div>
        
      </div>

      {/* Grid Description */}
      {properties.basic.description && !isCollapsed && (
        <div className="grid-description" style={{ marginTop: '8px', color: 'var(--df-color-text-light)', fontSize: '14px' }}>
          {properties.basic.description}
        </div>
      )}

      {/* Grid Content */}
      {!isCollapsed && (
        <div className="grid-content" style={{ marginTop: '16px' }}>
          {mode === 'edit' ? (
            <GridDropZone
              gridComponents={gridComponents}
              mode={mode}
              onComponentSelect={onComponentSelect || (() => {})}
              onComponentDelete={handleComponentDelete}
              onComponentEdit={onComponentEdit}
              onComponentUpdate={handleComponentUpdate}
              selectedComponent={selectedComponent || null}
              renderFormComponent={renderFormComponent || renderComponent}
              gridId={id}
            />
          ) : (
            /* Table View for Preview/Test Mode */
            <TableView
              templateComponents={gridComponents}
              dataEntries={dataEntries}
              renderFormComponent={renderFormComponent || renderComponent}
              mode={mode}
              allowAddRemoveEntries={properties.datagrid?.allowAddRemoveEntries ?? true}
              addAnotherText={properties.datagrid?.addAnotherText ?? 'Add Entry'}
              removeText={properties.datagrid?.removeText ?? 'Remove'}
              maxEntries={properties.datagrid?.maxEntries ?? 10}
              minEntries={properties.datagrid?.minEntries ?? 1}
              displayAsGrid={properties.datagrid?.displayAsGrid ?? true}
              onAddEntry={onEntryAdd ? onEntryAdd : handleAddEntry}
              onRemoveEntry={onEntryRemove ? onEntryRemove : handleRemoveEntry}
              formData={formData}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default DfFormDataGrid